import { Link } from "react-router-dom";
import { ArrowRight, Globe } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useGetProhibitedKeywords } from "@/resources/prohibited-keyword/prohibited-keyword.api";
import { useAppData } from "@/contexts/AppDataContext";
import { AddProhibitedKeywordsForm } from "./AddProhibitedKeywordsForm";
import { ProhibitedKeywordsList } from "./ProhibitedKeywordsList";

interface BrandProhibitedKeywordsPanelProps {
  brandId: number | null;
}

export function BrandProhibitedKeywordsPanel({ brandId }: BrandProhibitedKeywordsPanelProps) {
  const { brands } = useAppData();
  const brandLabel = brands.find((b) => b.id === brandId)?.name ?? "this brand";

  const { data: keywords = [], isLoading } = useGetProhibitedKeywords(brandId);

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Prohibited Keywords (Brand-Specific)</h2>
        <p className="text-sm text-muted-foreground mt-1">
          These keywords apply only to {brandLabel} — they are not global. Signal AI will avoid
          them in generated search terms, scorecards, and A/B test copy for this brand.
        </p>
      </div>

      <AddProhibitedKeywordsForm brandId={brandId} existingKeywords={keywords} />

      {/* AUT-41: point users to the platform-wide (Universal) list in Global Settings. */}
      <Link
        to="/settings?tab=prohibited-keywords"
        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-tint-soft px-3 py-2.5 hover:bg-muted transition-colors group"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-muted-foreground">
            Looking for global prohibited keywords?{" "}
            <span className="font-medium text-foreground">Manage them here.</span>
          </span>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </Link>

      <ProhibitedKeywordsList
        brandId={brandId}
        brandLabel={brandLabel}
        keywords={keywords}
        isLoading={isLoading}
      />
    </Card>
  );
}
